import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp'
import { sendExistingAccountCode, verifyExistingAccountCode } from '@/models/funnelSession'

const CODE_LENGTH = 6

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail
    if (detail) return detail
  }
  return fallback
}

/**
 * This DOT already belongs to a Cai account, so the visitor is most likely the
 * customer themselves. Rather than dead-ending them, mail a code to the address
 * already on file and sign them back in. The address is only ever shown masked —
 * anyone can type a DOT into the funnel, and a guess must not harvest an email.
 */
export function ClaimedDotSignIn({ sessionId }: { sessionId: string }) {
  const [code, setCode] = useState('')

  const send = useMutation({
    mutationFn: () => sendExistingAccountCode(sessionId),
    onSuccess: () => setCode(''),
  })
  const verify = useMutation({
    mutationFn: (value: string) => verifyExistingAccountCode(sessionId, value),
    onSuccess: (loginUrl) => {
      // Replace, not push: back from the app must not land on this spent funnel page.
      window.location.replace(loginUrl)
    },
  })

  const isVerifying = verify.isPending || verify.isSuccess
  const isSending = send.isPending
  const error = verify.isError
    ? errorMessage(verify.error, 'That code did not work. Please try again.')
    : send.isError
      ? errorMessage(send.error, "We couldn't send that code. Please try again.")
      : undefined

  function handleChange(value: string) {
    setCode(value)
    if (value.length === CODE_LENGTH && !isVerifying) verify.mutate(value)
  }

  if (!send.data) {
    return (
      <div className="flex flex-col gap-3">
        {error && (
          <Banner variant="error" small>
            {error}
          </Banner>
        )}
        <Button
          onClick={() => send.mutate()}
          loading={isSending ? 'Sending your code…' : undefined}
          className="w-full"
        >
          Email me a code
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <p className="text-muted-foreground text-center text-sm">
        We sent a {CODE_LENGTH}-digit code to {send.data}. Enter it to get back into your account.
      </p>

      <InputOTP
        maxLength={CODE_LENGTH}
        value={code}
        onChange={handleChange}
        autoComplete="one-time-code"
        autoFocus
        disabled={isVerifying}
      >
        <InputOTPGroup>
          {Array.from({ length: CODE_LENGTH }).map((_, index) => (
            <InputOTPSlot key={index} index={index} />
          ))}
        </InputOTPGroup>
      </InputOTP>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <Button
        variant="ghost"
        className="text-sm"
        disabled={isVerifying}
        loading={isSending ? 'Sending…' : undefined}
        onClick={() => {
          verify.reset()
          send.mutate()
        }}
      >
        Send a new code
      </Button>
    </div>
  )
}
